import { supabase } from '@/lib/supabase';
import { toUserMessage } from '@/lib/errors';
import { log } from '@/lib/logger';

/** Respuesta estandarizada (P-API-01). */
export type ApiSuccess<T> = { ok: true; data: T; meta?: Record<string, unknown> };
export type ApiError = {
  ok: false;
  error: { code: string; message: string; details?: unknown };
};
export type ApiResponse<T> = ApiSuccess<T> | ApiError;

export const ok = <T>(data: T, meta?: Record<string, unknown>): ApiSuccess<T> => ({
  ok: true,
  data,
  ...(meta ? { meta } : {}),
});

export const err = (code: string, message: string, details?: unknown): ApiError => ({
  ok: false,
  error: { code, message, ...(details !== undefined ? { details } : {}) },
});

/** Wrapper tipado para llamar RPCs con handling consistente.
 *  Acepta tanto nombres tipados (auto-complete desde Database['public']['Functions'])
 *  como strings arbitrarios (para RPCs que aún no están en los types generados). */
export async function rpc<T = unknown>(
  name: string,
  args: Record<string, unknown> = {},
): Promise<ApiResponse<T>> {
  // Cast necesario: supabase.rpc() espera un literal union; nosotros trabajamos
  // con nombres dinámicos para no acoplar cada call site a los types generados.
  // Seguridad: la llamada igual valida los args contra el schema real en runtime.
  const { data, error } = await (
    supabase.rpc as unknown as (
      fn: string,
      a: Record<string, unknown>,
    ) => Promise<{
      data: unknown;
      error: { code?: string; message: string } | null;
    }>
  )(name, args);
  if (error) {
    log('error', 'rpc', `${name} falló`, { error, args });
    return err(error.code ?? 'RPC_ERROR', toUserMessage(error), error);
  }
  return ok(data as T);
}
